/**
 * CreditNotesListView
 * Shows credit notes list (used by RechercheView)
 * Invoice selection now happens inside the CreateCreditNoteModal
 */

import React, { useState } from 'react';
import {
  ArrowDownTrayIcon,
  XCircleIcon,
  PaperAirplaneIcon,
  PlusIcon,
  DocumentTextIcon,
  DocumentPlusIcon,
  BuildingLibraryIcon,
  EnvelopeIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import { useCreditNotes, useFinalizeCreditNote, useCancelCreditNote } from '../../hooks/useCreditNotes';
import { downloadCreditNotePdf } from '../../api/facturation.api';
import type { CreditNote, CreditNoteStatus, CreditNoteFilters, InvoiceFilters } from '../../types/facturation.types';
import { CreateCreditNoteModal } from './CreateCreditNoteModal';
import { CreateCustomCreditNoteModal } from '../CustomCreditNote/CreateCustomCreditNoteModal';
import { CreditNoteDetailModal } from '../CreditNoteDetailModal/CreditNoteDetailModal';
import { TableSkeletonLoader } from '../SkeletonLoader';
import { formatDate } from '../../utils/dateFormat';
import '../../facturation.css';

// Status badge component
const StatusBadge: React.FC<{ status: CreditNoteStatus }> = ({ status }) => {
  const statusClass = status.toLowerCase();
  const labels: Record<CreditNoteStatus, string> = {
    DRAFT: 'Brouillon',
    GENERATED: 'Genere',
    SENT: 'Envoye',
    CANCELLED: 'Annule',
  };

  return (
    <span className={`fact-status-badge ${statusClass}`}>
      {labels[status]}
    </span>
  );
};

// Format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
};

interface SearchContext {
  mode: 'affaire' | 'client' | 'facture';
  term: string;
}

interface CreditNotesListViewProps {
  initialFilters: InvoiceFilters;
  searchLabel?: string;
  /** Search context from parent to pass to CreateCreditNoteModal */
  searchContext?: SearchContext;
}

export const CreditNotesListView: React.FC<CreditNotesListViewProps> = ({
  initialFilters: _initialFilters,
  searchLabel: _searchLabel,
  searchContext,
}) => {
  // Credit notes list filters - include affaireId from searchContext
  const baseFilters: CreditNoteFilters = {
    page: 1,
    limit: 20,
    // Only filter by affaire when searching by affaire ID
    affaireId: searchContext?.mode === 'affaire' ? searchContext.term : undefined,
  };
  const [filters, setFilters] = useState<CreditNoteFilters>(baseFilters);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCustomCreditNoteModal, setShowCustomCreditNoteModal] = useState(false);
  const [selectedCreditNote, setSelectedCreditNote] = useState<CreditNote | null>(null);

  // Update filters when searchContext changes
  React.useEffect(() => {
    setFilters(prev => ({
      ...prev,
      affaireId: searchContext?.mode === 'affaire' ? searchContext.term : undefined,
      page: 1, // Reset to first page when search changes
    }));
  }, [searchContext?.mode, searchContext?.term]);

  // Fetch credit notes
  const { data: creditNotesData, isLoading, refetch } = useCreditNotes(filters);
  const finalizeMutation = useFinalizeCreditNote();
  const cancelMutation = useCancelCreditNote();

  const handleStatusFilter = (status: CreditNoteStatus | undefined) => {
    setFilters({ ...filters, status, page: 1 });
  };

  const handleDownloadPdf = async (creditNote: CreditNote) => {
    try {
      const blob = await downloadCreditNotePdf(creditNote.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = creditNote.credit_note_number
        ? `note_credit_${creditNote.credit_note_number.replace(/\//g, '-')}.pdf`
        : `note_credit_brouillon_${creditNote.id}.pdf`;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 100); // Delay for download to start
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleFinalize = async (id: number) => {
    if (confirm('Finaliser cette note de credit ? Un PDF sera genere et envoye a Falco.')) {
      try {
        await finalizeMutation.mutateAsync(id);
        refetch();
      } catch (error) {
        console.error('Finalize failed:', error);
      }
    }
  };

  const handleCancel = async (id: number) => {
    const reason = prompt('Raison de l\'annulation:');
    if (reason) {
      try {
        await cancelMutation.mutateAsync({ id, reason });
        refetch();
      } catch (error) {
        console.error('Cancel failed:', error);
      }
    }
  };

  const handleCreditNoteSuccess = () => {
    refetch();
    setShowCreateModal(false);
  };

  const handleRowClick = (creditNote: CreditNote) => {
    // Allow clicking on any non-cancelled credit note
    if (creditNote.status !== 'CANCELLED') {
      setSelectedCreditNote(creditNote);
    }
  };

  const handleDetailModalSuccess = () => {
    refetch();
    setSelectedCreditNote(null);
  };

  return (
    <div className="fact-invoices-list">
      {/* Controls: Status Filters + Create Button */}
      <div className="fact-controls">
        <div className="fact-controls-left">
          <div className="fact-status-filters">
            <button
              className={`fact-status-btn ${!filters.status ? 'active' : ''}`}
              onClick={() => handleStatusFilter(undefined)}
            >
              Tous
            </button>
            <button
              className={`fact-status-btn ${filters.status === 'DRAFT' ? 'active' : ''}`}
              onClick={() => handleStatusFilter('DRAFT')}
            >
              Brouillons
            </button>
            <button
              className={`fact-status-btn ${filters.status === 'GENERATED' ? 'active' : ''}`}
              onClick={() => handleStatusFilter('GENERATED')}
            >
              Generes
            </button>
            <button
              className={`fact-status-btn ${filters.status === 'SENT' ? 'active' : ''}`}
              onClick={() => handleStatusFilter('SENT')}
            >
              Envoyes
            </button>
          </div>
        </div>

        <div className="fact-controls-right">
          <div className="relative">
            <button
              className="fact-btn fact-btn-primary"
              onClick={() => setShowCreateMenu(!showCreateMenu)}
            >
              <PlusIcon className="w-5 h-5" />
              <span>Nouvelle note de credit</span>
            </button>

            {showCreateMenu && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowCreateMenu(false)}
                />
                <div className="fact-dropdown-menu">
                  <button
                    onClick={() => {
                      setShowCreateModal(true);
                      setShowCreateMenu(false);
                    }}
                    className="fact-dropdown-item"
                  >
                    <DocumentPlusIcon className="fact-dropdown-item-icon" />
                    <div className="fact-dropdown-item-content">
                      <span className="fact-dropdown-item-title">Note de credit</span>
                      <span className="fact-dropdown-item-desc">Liee a une facture existante</span>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      setShowCustomCreditNoteModal(true);
                      setShowCreateMenu(false);
                    }}
                    className="fact-dropdown-item"
                  >
                    <DocumentTextIcon className="fact-dropdown-item-icon" />
                    <div className="fact-dropdown-item-content">
                      <span className="fact-dropdown-item-title">Note de credit personnalisee</span>
                      <span className="fact-dropdown-item-desc">Sans lien avec une facture</span>
                    </div>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Credit Notes List */}
      <div className="fact-list">
        {isLoading ? (
          <TableSkeletonLoader rows={5} columns={[100, 100, 200, 100, 100, 100, 80]} />
        ) : !creditNotesData?.creditNotes || creditNotesData.creditNotes.length === 0 ? (
          <div className="fact-empty">
            <DocumentTextIcon className="w-12 h-12" />
            <h3>Aucune note de credit</h3>
            <p>Cliquez sur "Nouvelle note de credit" pour en creer une.</p>
          </div>
        ) : (
          <div className="fact-results-container">
            <div className="fact-table fact-table--cn">
              <div className="fact-table-header">
                <div>Numero</div>
                <div>Facture</div>
                <div>Client</div>
                <div>Créé par</div>
                <div>Date</div>
                <div style={{ textAlign: 'right' }}>Montant</div>
                <div>Statut</div>
                <div style={{ textAlign: 'right' }}>Actions</div>
              </div>

              {creditNotesData.creditNotes.map((creditNote) => (
                <div
                  key={creditNote.id}
                  className={`fact-table-row ${creditNote.status !== 'CANCELLED' ? 'clickable' : ''}`}
                  onClick={() => handleRowClick(creditNote)}
                >
                  <div className="fact-invoice-number">
                    {creditNote.credit_note_number}
                  </div>
                  <div className="fact-affaire-number">
                    {creditNote.original_invoice_number || '-'}
                  </div>
                  <div className="fact-col-client">
                    <span className="fact-client-name">{creditNote.billing_name || '-'}</span>
                  </div>
                  <div className="fact-col-creator">
                    {creditNote.first_name || creditNote.last_name ? (
                      <span title={`Créé le ${new Date(creditNote.created_at).toLocaleString('fr-BE')}`}>
                        {creditNote.first_name || ''} {creditNote.last_name || ''}
                      </span>
                    ) : (
                      <span className="fact-no-creator">-</span>
                    )}
                  </div>
                  <div className="fact-col-date">
                    {formatDate(creditNote.created_at)}
                  </div>
                  <div className="fact-col-amount" style={{ color: '#dc2626' }}>
                    -{formatCurrency(creditNote.total_tvac)}
                  </div>
                  <div className="fact-col-status">
                    <StatusBadge status={creditNote.status} />
                    {/* Delivery badges - show Falco, Email, Peppol status for non-DRAFT */}
                    {creditNote.status !== 'DRAFT' && (
                      <div className="fact-delivery-badges">
                        {/* Falco badge */}
                        <span
                          className={`fact-delivery-badge ${
                            creditNote.falco_status === 'SENT' || creditNote.falco_status === 'UPLOADED'
                              ? 'success'
                              : creditNote.falco_status === 'FAILED'
                              ? 'error'
                              : creditNote.falco_status === 'PENDING' || creditNote.falco_status === 'PROCESSING'
                              ? 'pending'
                              : 'inactive'
                          }`}
                          title={`Falco: ${creditNote.falco_status || 'Non envoyé'}`}
                        >
                          <BuildingLibraryIcon className="w-3.5 h-3.5" />
                        </span>
                        {/* Email badge */}
                        <span
                          className={`fact-delivery-badge ${
                            creditNote.email_sent
                              ? 'success'
                              : creditNote.email_error
                              ? 'error'
                              : 'inactive'
                          }`}
                          title={
                            creditNote.email_sent
                              ? `Email envoyé${creditNote.email_recipient ? ` à ${creditNote.email_recipient}` : ''}${creditNote.email_sent_at ? ` le ${new Date(creditNote.email_sent_at).toLocaleDateString('fr-BE')}` : ''}`
                              : creditNote.email_error
                              ? `Erreur email: ${creditNote.email_error}`
                              : 'Email non envoyé'
                          }
                        >
                          <EnvelopeIcon className="w-3.5 h-3.5" />
                        </span>
                        {/* Peppol badge */}
                        <span
                          className={`fact-delivery-badge ${
                            creditNote.peppol_status === 'DELIVERED'
                              ? 'success'
                              : creditNote.peppol_status === 'FAILED'
                              ? 'error'
                              : creditNote.peppol_status === 'PENDING'
                              ? 'pending'
                              : 'inactive'
                          }`}
                          title={`Peppol: ${
                            creditNote.peppol_status === 'NOT_REGISTERED'
                              ? 'Client non inscrit'
                              : creditNote.peppol_status || 'N/A'
                          }`}
                        >
                          <GlobeAltIcon className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    )}
                    {/* Validator info - show who validated and when */}
                    {creditNote.validated_by && creditNote.validated_at && (
                      <span
                        className="fact-validator-info"
                        title={`Validé le ${new Date(creditNote.validated_at).toLocaleString('fr-BE')}`}
                      >
                        par {creditNote.validator_first_name || ''} {creditNote.validator_last_name || ''}
                      </span>
                    )}
                  </div>
                  <div className="fact-col-actions">
                    {creditNote.pdf_path && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDownloadPdf(creditNote); }}
                        className="fact-action-btn download"
                        title="Telecharger PDF"
                      >
                        <ArrowDownTrayIcon />
                      </button>
                    )}
                    {creditNote.status === 'DRAFT' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleFinalize(creditNote.id); }}
                        className="fact-action-btn finalize"
                        title="Finaliser"
                      >
                        <PaperAirplaneIcon />
                      </button>
                    )}
                    {creditNote.status !== 'CANCELLED' && creditNote.status !== 'SENT' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleCancel(creditNote.id); }}
                        className="fact-action-btn cancel"
                        title="Annuler"
                      >
                        <XCircleIcon />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pagination */}
        {creditNotesData && creditNotesData.totalPages > 1 && (
          <div className="fact-pagination">
            <button
              onClick={() => setFilters({ ...filters, page: (filters.page || 1) - 1 })}
              disabled={creditNotesData.page <= 1}
              className="fact-page-btn"
            >
              Precedent
            </button>
            <span className="fact-page-info">
              Page {creditNotesData.page} / {creditNotesData.totalPages}
            </span>
            <button
              onClick={() => setFilters({ ...filters, page: (filters.page || 1) + 1 })}
              disabled={creditNotesData.page >= creditNotesData.totalPages}
              className="fact-page-btn"
            >
              Suivant
            </button>
          </div>
        )}
      </div>

      {/* Create Credit Note Modal - uses searchContext if provided */}
      <CreateCreditNoteModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreditNoteSuccess}
        searchContext={searchContext?.mode !== 'facture' ? { mode: searchContext!.mode, term: searchContext!.term } : undefined}
      />

      {/* Custom Credit Note Modal */}
      <CreateCustomCreditNoteModal
        isOpen={showCustomCreditNoteModal}
        onClose={() => setShowCustomCreditNoteModal(false)}
        onSuccess={handleCreditNoteSuccess}
        affaireId={searchContext?.mode === 'affaire' ? searchContext.term : undefined}
      />

      {/* Credit Note Detail Modal */}
      {selectedCreditNote && (
        <CreditNoteDetailModal
          creditNote={selectedCreditNote}
          isOpen={!!selectedCreditNote}
          onClose={() => setSelectedCreditNote(null)}
          onSuccess={handleDetailModalSuccess}
        />
      )}
    </div>
  );
};

export default CreditNotesListView;
